import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  buyerSubmissionsApi,
  type BuyerSubmissionListParams,
  type BuyerSubmissionSummaryDto,
} from "@/api/buyers";
import {
  SearchField,
  SortSelect,
  SubTable,
  SubTableFooter,
  SUB_TABLE_CHROME_THRESHOLD,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { useDebounced } from "@/hooks/buyers/useSmartImport";

import { resolveSort, SUBMISSION_SORTS } from "../buyerFilters";

/**
 * A record's own slice of the submissions list — a buyer's, or a listing's.
 *
 * 🔴 THIS FIXES A SILENT DATA-VISIBILITY DEFECT, and the styling is incidental.
 *
 * Both surfaces used to hardcode `{ page: 1, pageSize: 50 }` and read only
 * `data.data`, discarding `totalCount`. So a buyer with 51 submissions rendered
 * 50 of them, with no pager, no caption, and nothing anywhere on the screen
 * saying a row had been withheld. The 51st was simply gone. A listing presented
 * to 51 buyers did the same. Neither is a styling problem; a table conversion
 * is only how it was found.
 *
 * The server needed NO change to fix it. `BuyerSubmissionService.ListAsync`
 * has always paged (`Skip`/`Take`) and sorted, and its `ApplySort` already
 * offers `status` ranked in SQL — not alphabetically — with a unique `Id`
 * tiebreak on every arm, which is what makes paging safe here: split queries
 * with `Skip`/`Take` need a total ordering or two pages can disagree about
 * which rows they contain. The client was throwing all of that away.
 *
 * 🔴 SERVER-BACKED, NOT DECORATIVE. `chromeThreshold` is the page size, so
 * `SubTable`'s own search / sort / footer — which act on the ARRAY IT HOLDS —
 * can never activate. A client-side pager over one already-sliced page would
 * slice it again and disagree with the real one; a client-side sort would
 * reorder 25 rows out of 200 while claiming to have ordered the list. The sort
 * control and the footer here both go to the server, where they can tell the
 * truth. Same reasoning, same idiom, as `BuyersListPage`.
 */
export const RECORD_SUBMISSIONS_PAGE_SIZE = 25;

/** How long the search waits for typing to settle — the agent picker's pace. */
const SEARCH_DEBOUNCE_MS = 250;

export type RecordSubmissionScope =
  | { buyerLeadId: string; listingId?: never }
  | { listingId: string; buyerLeadId?: never };

export interface RecordSubmissions {
  rows: BuyerSubmissionSummaryDto[];
  /** The TRUE total from the server, not the number of rows on screen. */
  total: number;
  page: number;
  setPage: (page: number) => void;
  sort: string;
  setSort: (sort: string) => void;
  /** §5 — sort and the footer arrive together, and not before. */
  hasChrome: boolean;
  /** The list is empty only because Show archived is off (D4). */
  hasHiddenArchived: boolean;
  /** Addendum B (owner ruling): the server-side search, as typed. Its debounced,
   *  trimmed value narrows the list (and the total) on the server. */
  search: string;
  setSearch: (next: string) => void;
  /** The search the current rows were filtered by — non-empty means an empty
   *  list says "nothing matches", never "no submissions". */
  activeSearch: string;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  refetch: () => void;
}

/**
 * Paging and ordering for one record's submissions, both server-side.
 *
 * <p>Deliberately NOT URL-backed. A record page's `?tab=` and `?submission=`
 * parameters are its own; adding a page number would put a transient scroll
 * position into every link a specialist shares, and the drawer parameter these
 * tables write is the one that has to survive a refresh.</p>
 *
 * <p>⚠️ <b>ONE LIST ON THE SAME RECORD PAGE DELIBERATELY REVERSES THIS: the
 * Research tab's candidate list (R20)</b>, which keeps its filters, sort, page
 * and page size in `r…` URL keys (`research/researchListFilters.ts`). The
 * difference is size and cost of losing your place: a run holds hundreds of
 * rows filtered on the server, and a reload or a Back that dropped a reader
 * onto page 1 of a different filter loses real work, where a record's handful
 * of submissions does not. Both reasons above still hold for THIS list, which
 * is why it stays in React state — the reversal is scoped to that one list and
 * is not a new rule for record pages. The price it pays is named there: every
 * one of its keys has to be deleted when the reader leaves the tab.</p>
 */
export function useRecordSubmissions(
  scope: RecordSubmissionScope,
  /** D4 "Show archived" — the HOST owns where this state lives (the link on a
   *  buyer's tab, local state on a listing, R4). Off hides archived rows. */
  { includeArchived = false }: { includeArchived?: boolean } = {},
): RecordSubmissions {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<string>(SUBMISSION_SORTS[0].value);
  const [search, setSearch] = useState("");
  const activeSearch = useDebounced(search, SEARCH_DEBOUNCE_MS).trim();

  const { buyerLeadId, listingId } = scope;

  /*
    🔴 Page three of one record is not page three of the next. This hook's state
    is React state, so it OUTLIVES a change of record wherever the host stays
    mounted — a listing drawer retargeted at a second listing, a route whose
    `:id` changes without unmounting the page. The reader lands on an empty
    table with a pager that says "Page 3 of 1", which reads as a broken record
    rather than a stale page number.

    Derived-during-render rather than an effect: an effect would paint the empty
    page first and then correct it, which is the flicker in a slower costume.
  */
  // Show archived and the search change the set too, so page 3 of one is not
  // page 3 of the other.
  const scopeKey = `${buyerLeadId ?? listingId ?? ""}:${includeArchived}:${activeSearch}`;
  const [lastScope, setLastScope] = useState(scopeKey);
  if (lastScope !== scopeKey) {
    setLastScope(scopeKey);
    setPage(1);
  }

  const params = useMemo(
    () =>
      ({
        ...(buyerLeadId ? { buyerLeadId } : { listingId }),
        page,
        pageSize: RECORD_SUBMISSIONS_PAGE_SIZE,
        ...resolveSort(SUBMISSION_SORTS, sort),
        ...(includeArchived ? { includeArchived: true } : {}),
        ...(activeSearch ? { q: activeSearch } : {}),
        // `sortBy`/`sortDirection` live on the server's `PagedQuery` and are read
        // by `ApplySort`, but the shared client mirror never declared them —
        // the same omission `BuyerSubmissionsListPage` documents. Widening
        // `BuyerSubmissionListParams` is the real fix and belongs with that
        // mirror; this keeps the cast local and narrow rather than silently
        // dropping the ordering the reader chose.
      }) as BuyerSubmissionListParams,
    [buyerLeadId, listingId, page, sort, includeArchived, activeSearch],
  );

  const query = useQuery({
    queryKey: buyerSubmissionKeys.list(params),
    queryFn: () => buyerSubmissionsApi.list(params),
    // A page change would otherwise blank the table to a skeleton and bounce
    // the reader to the top of a tab they are working down.
    placeholderData: (prev) => prev,
  });

  // 🔴 Memoised, not `query.data?.data ?? []` inline: that allocates a fresh
  // array on every render while the query is in flight, and a consumer that
  // keys an effect on `rows` (the combined intro selection does) would then
  // re-run it every render. See `buyerSelection.ts` for what that cost once.
  const rows = useMemo(() => query.data?.data ?? [], [query.data]);
  const total = query.data?.totalCount ?? 0;

  /*
    🔴 "No submissions yet" must be TRUE. With archived rows hidden, an empty
    list can mean "never presented" or "every one is archived" — and telling a
    specialist to present this buyer when every pairing is archived sends them
    into an "Already presented" refusal. So an empty hidden-archived list asks
    once whether archived rows exist (one row is enough).
  */
  const archivedProbeParams = useMemo(
    () =>
      ({
        ...(buyerLeadId ? { buyerLeadId } : { listingId }),
        page: 1,
        pageSize: 1,
        includeArchived: true,
      }) as BuyerSubmissionListParams,
    [buyerLeadId, listingId],
  );
  const archivedProbe = useQuery({
    queryKey: buyerSubmissionKeys.list(archivedProbeParams),
    queryFn: () => buyerSubmissionsApi.list(archivedProbeParams),
    enabled: !includeArchived && !activeSearch && query.isSuccess && total === 0,
  });
  const hasHiddenArchived =
    !includeArchived && !activeSearch && total === 0 && (archivedProbe.data?.totalCount ?? 0) > 0;

  return {
    rows,
    total,
    page,
    setPage,
    sort,
    setSort,
    hasChrome: total >= SUB_TABLE_CHROME_THRESHOLD,
    hasHiddenArchived,
    search,
    setSearch,
    activeSearch,
    isLoading: query.isLoading,
    isError: query.isError || (!query.isLoading && !query.data),
    isFetching: query.isFetching,
    refetch: () => void query.refetch(),
  };
}

/**
 * The table itself, with the sort control above it and the pager below.
 *
 * <p>Columns are the caller's, and stay the caller's. A buyer's submissions are
 * headed by the LISTING and carry its asking price; a listing's are headed by
 * the BUYER and carry no asking price, because every row would repeat the same
 * one. Adopting shared chrome is about how a table looks and when it grows
 * controls, not about how much a row is allowed to say.</p>
 */
export function RecordSubmissionsTable({
  state,
  columns,
  caption,
  sortLabel,
  testId,
  rowTestId,
  rowSelected,
  searchPlaceholder,
}: {
  state: RecordSubmissions;
  columns: SubTableColumn<BuyerSubmissionSummaryDto>[];
  caption: string;
  sortLabel: string;
  testId?: string;
  rowTestId?: string;
  /** Tints the rows a caller has ticked. Absent where nothing can be ticked. */
  rowSelected?: (row: BuyerSubmissionSummaryDto) => boolean;
  /** Turns on the server-side search box (Addendum B — a buyer's tab). It
   *  appears with the rest of the chrome (§5), and stays while a search is
   *  typed so narrowing below the threshold cannot pull it away. */
  searchPlaceholder?: string;
}) {
  const { rows, total, page, setPage, sort, setSort, hasChrome, search, setSearch, activeSearch } = state;
  const first = (page - 1) * RECORD_SUBMISSIONS_PAGE_SIZE + 1;
  const showSearch = Boolean(searchPlaceholder) && (hasChrome || search !== "" || activeSearch !== "");

  return (
    <div className="flex min-w-0 flex-col gap-space-3">
      {showSearch && (
        <SearchField
          value={search}
          onChange={setSearch}
          placeholder={searchPlaceholder!}
          label={`Search ${caption}`}
          className="max-w-sm"
        />
      )}
      {hasChrome && (
        <div className="flex justify-end">
          <SortSelect
            value={sort}
            onChange={(next) => {
              setSort(next);
              // Page 3 of the old ordering is not page 3 of the new one.
              setPage(1);
            }}
            options={SUBMISSION_SORTS}
            label={sortLabel}
          />
        </div>
      )}

      <SubTable<BuyerSubmissionSummaryDto>
        caption={caption}
        columns={columns}
        rows={rows}
        rowKey={(s) => s.id}
        rowTestId={rowTestId ? () => rowTestId : undefined}
        rowSelected={rowSelected}
        chromeThreshold={RECORD_SUBMISSIONS_PAGE_SIZE}
        empty={activeSearch ? { message: `Nothing matches “${activeSearch}”.` } : undefined}
        data-testid={testId}
      />

      {hasChrome && (
        <SubTableFooter
          firstShown={total === 0 ? 0 : first}
          lastShown={first + rows.length - 1}
          total={total}
          page={page}
          totalPages={Math.max(1, Math.ceil(total / RECORD_SUBMISSIONS_PAGE_SIZE))}
          onPrevious={() => setPage(Math.max(1, page - 1))}
          onNext={() =>
            setPage(
              Math.min(Math.max(1, Math.ceil(total / RECORD_SUBMISSIONS_PAGE_SIZE)), page + 1),
            )
          }
        />
      )}
    </div>
  );
}
